import { APP_VERSION } from "../version.js";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase-config.js";

// GLOBAL HELPER: Sync Series Stats
window.syncStats = async (btn) => {
    if (btn.getAttribute('data-processing') === 'true') return;
    btn.setAttribute('data-processing', 'true');
    btn.style.opacity = '0.5';
    const originalText = btn.innerHTML;
    btn.innerHTML = `<span class="loader-spin w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>`;

    try {
        const { ContentService } = await import("../services/content.service.js"); // Dynamic import
        const count = await ContentService.syncSeriesStats();
        await window.Modal.alert(`Sync Complete.\nUpdated stats for ${count} series.`);
        btn.innerHTML = `<span class="material-symbols-outlined text-sm">check</span>`;
        setTimeout(() => {
            btn.removeAttribute('data-processing');
            btn.style.opacity = '1';
            btn.innerHTML = originalText;
        }, 2000);
    } catch (e) {
        console.error(e);
        await window.Modal.alert("Sync Failed: " + e.message);
        btn.removeAttribute('data-processing');
        btn.style.opacity = '1';
        btn.innerHTML = originalText;
    }
};

// GLOBAL HELPER: Sync Users
window.syncUsers = async (btn) => {
    // ... (logic handled inside component usually, but keeping global for button)
    // Actually, let's keep it simple or implement if needed. 
    // The previous code had it defined or referenced. For now, empty or basic alert as placeholder if not used.
    // Wait, the previous file had it. I should preserve it if I can.
    // It's not critical for Overview, but "Sync Missing Users" button uses it.
    // Let's implement a basic version or just alert.
    // Actually, looking at previous code, it was empty in the view.
    if (btn.getAttribute('data-processing') === 'true') return;
    btn.setAttribute('data-processing', 'true');
    try {
        const { UserService } = await import("../services/user.service.js");
        await UserService.syncMissingUsers();
        await window.Modal.alert("Users Synced.");
    } catch (e) {
        console.error(e);
        await window.Modal.alert("Sync Failed: " + e.message);
    } finally {
        btn.removeAttribute('data-processing');
    }
};

export const AdminDashboardPage = async () => {
    // [FIX v1.36.471] Non-Blocking Render Pattern
    // Eliminate Router Promise Hangs by returning container immediately.
    const container = document.createElement('div');
    container.className = 'min-h-screen flex items-center justify-center bg-gray-50';
    container.innerHTML = `<div class="text-center"><span class="loader-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin block mx-auto mb-4"></span><p class="text-gray-500 font-medium">관리자 권한 확인 중...</p></div>`;

    const initAuth = async () => {
        const auth = getAuth(app);
        let user = auth.currentUser;

        // 1. Fast Path
        if (user) {
            if (isValidAdmin(user)) {
                await renderAdminDashboard(user, container);
                return;
            }
        }

        // 2. Listener Path (Fallback)
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            unsubscribe();
            if (isValidAdmin(authUser)) {
                await renderAdminDashboard(authUser, container);
            } else {
                window.router.navigate('/');
            }
        });
    };

    // Helper: Admin Validation
    const isValidAdmin = (u) => {
        return u && (u.role === 'admin' || u.isSuperAdmin || u.email === 'hegler.kim@gmail.com');
    };

    // Helper: Render to existing container
    const renderAdminDashboard = async (user, targetContainer) => {
        try {
            // Clear Loading State
            targetContainer.innerHTML = '';
            targetContainer.className = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen bg-gray-50';

            // Re-use existing render logic (We need to modify the original function to accept container or move logic here)
            // ... Actually, renderAdminDashboard below creates a NEW container.
            // We should modify renderAdminDashboard to accepting a container or just append its result.
            // Let's call the original render function logic here.

            // Since we cannot easily modify 'const renderAdminDashboard' signature below without replacing huge chunk,
            // we will append the result of a modified logic or just copy usage.

            // Wait, I can't access 'renderAdminDashboard' defined below if I don't hoist it or change it.
            // It is defined as 'const' below. Access before initialization error?
            // Correct. I need to move 'renderAdminDashboard' definition UP or change how I call it.

            // STRATEGY: 
            // 1. Rename the exported function to AdminDashboardPageController.
            // 2. Keep renderAdminDashboard as is.
            // 3. Call renderAdminDashboard and REPLACE content of container.

            const dashboardContent = await _internalRenderDashboard(user);
            targetContainer.innerHTML = ''; // Ensure clear
            targetContainer.className = ''; // Reset class to allow dashboard to control it? 
            // The dashboardContent is a DIV with classes.
            // We should just append dashboardContent children or replace container?
            // Router expects 'container' to be the root.

            // If _internalRenderDashboard returns a 'div' (container), we can move its children and classes.
            targetContainer.className = dashboardContent.className;
            targetContainer.innerHTML = dashboardContent.innerHTML;

            // Re-attach event listeners? 
            // _internalRenderDashboard sets onclick handlers. Moving path via innerHTML BREAKS events.
            // BAD IDEA.

            // BETTER STRATEGY:
            // Pass 'container' TO `_internalRenderDashboard`?
            // Or `targetContainer.appendChild(dashboardContent)`.
            // But targetContainer is the root.
            targetContainer.innerHTML = '';
            targetContainer.appendChild(dashboardContent);
            // This creates <div><div>...</div></div>. 
            // Acceptable for a hotfix.
        } catch (e) {
            console.error("Dashboard Init Error", e);
            targetContainer.innerHTML = '<div class="text-red-500 p-10">Error loading dashboard.</div>';
        }
    };

    initAuth();
    return container;
};


const _internalRenderDashboard = async (user) => {


    // Configuration: Tab Definitions (Static Imports for Bundler)
    const TABS = [
        { id: 'overview', label: 'Overview', icon: null, load: () => import('./admin/tabs/OverviewTab.js'), exportName: 'OverviewTab' },
        { id: 'content', label: 'Content Moderation', icon: null, load: () => import('./admin/tabs/ContentTab.js'), exportName: 'ContentTab' },
        { id: 'requests', label: 'Author Requests', icon: null, load: () => import('./admin/tabs/AuthorRequestsTab.js'), exportName: 'AuthorRequestsTab' },
        { id: 'support', label: '1:1 문의', icon: 'support_agent', load: () => import('./admin/tabs/SupportTab.js'), exportName: 'SupportTab' },
        { id: 'reports', label: 'Report Center', icon: 'gavel', load: () => import('./admin/tabs/ReportsTab.js'), exportName: 'ReportsTab' },
        { id: 'analytics', label: 'Analytics & Trends', icon: 'trending_up', load: () => import('./admin/tabs/AnalyticsTab.js'), exportName: 'AnalyticsTab' }, // Renamed
        { id: 'settlement', label: 'Settlement', icon: 'payments', load: () => import('./admin/tabs/SettlementTab.js'), exportName: 'SettlementTab' }, // [NEW] Separated
        { id: 'strategy', label: 'Strategy', icon: 'psychology', load: () => import('./admin/tabs/StrategyTab.js'), exportName: 'StrategyTab' },
        { id: 'payment', label: 'Payment', icon: 'credit_card', load: () => import('./admin/tabs/PaymentTab.js'), exportName: 'PaymentTab' },
        { id: 'curation', label: 'Curation', icon: 'star', load: () => import('./admin/tabs/CurationTab.js'), exportName: 'CurationTab' },
        { id: 'marketing', label: 'Marketing', icon: 'campaign', load: () => import('./admin/tabs/MarketingTab.js'), exportName: 'MarketingTab' },
        { id: 'growth', label: 'Growth (SEO)', icon: 'rocket_launch', load: () => import('./admin/tabs/GrowthTab.js'), exportName: 'GrowthTab' },
        { id: 'proposals', label: 'Proposals', icon: 'lightbulb', load: () => import('./admin/tabs/ProposalsTab.js'), exportName: 'ProposalsTab' },
        { id: 'users', label: 'Users', icon: 'group', load: () => import('./admin/tabs/UsersTab.js'), exportName: 'UsersTab' },
        { id: 'rescue', label: 'Rescue & Recovery', icon: 'medical_services', load: () => import('./admin/tabs/RescueTab.js'), exportName: 'RescueTab' },
        { id: 'settings', label: 'Settings', icon: 'settings', load: () => import('./admin/tabs/SettingsTab.js'), exportName: 'SettingsTab' }
    ];

    const container = document.createElement('div');
    container.className = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen bg-gray-50';

    container.innerHTML = `
        <div class="mb-8">
            <h2 class="text-3xl font-bold text-gray-900">Admin Dashboard</h2>
            <p class="text-gray-500">Platform Management System <span class="text-xs bg-gray-200 px-2 py-1 rounded-full ml-2">${APP_VERSION}</span></p>
        </div>

        <!-- Tabs Container -->
        <!-- [FIX] Added overflow-x-auto for horizontal scrolling on small screens -->
        <div class="border-b border-gray-200 mb-6 overflow-x-auto scrollbar-hide">
            <nav class="-mb-px flex space-x-8 min-w-max px-1" aria-label="Tabs" id="admin-tabs-nav">
                <!-- Tabs injected via JS -->
            </nav>
        </div>

        <!-- Content Area -->
        <div id="admin-content-area" class="min-h-[400px]"></div>
    `;

    const nav = container.querySelector('#admin-tabs-nav');
    const contentArea = container.querySelector('#admin-content-area');

    // 1. Render Tabs
    TABS.forEach(tab => {
        const btn = document.createElement('button');
        btn.id = `tab-${tab.id}`;
        btn.className = "tab-btn border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1 transition-colors duration-200";
        btn.innerHTML = `
            ${tab.icon ? `<span class="material-symbols-outlined text-sm">${tab.icon}</span>` : ''}
            ${tab.label}
        `;

        // Click Handler
        btn.onclick = () => loadTab(tab);
        nav.appendChild(btn);
    });

    // 2. Tab Loader Logic
    const loadTab = async (tabConfig) => {
        // A. UI Reset
        container.querySelectorAll('.tab-btn').forEach(btn => {
            btn.classList.remove('border-blue-500', 'text-blue-600');
            btn.classList.add('border-transparent', 'text-gray-500');
        });

        // B. Active State
        const activeBtn = container.querySelector(`#tab-${tabConfig.id}`);
        if (activeBtn) {
            activeBtn.classList.remove('border-transparent', 'text-gray-500');
            activeBtn.classList.add('border-blue-500', 'text-blue-600');
        }

        // C. Loading State
        contentArea.innerHTML = '<div class="flex justify-center py-20"><span class="material-symbols-outlined animate-spin text-4xl text-gray-300">donut_large</span></div>';

        // D. Dynamic Import & Render
        try {
            // [FIX] Use explicit load function for bundler support
            const module = await tabConfig.load();
            const TabComponent = module[tabConfig.exportName];

            if (typeof TabComponent === 'function') {
                contentArea.innerHTML = ''; // Clear loader
                await TabComponent(contentArea);
            } else {
                throw new Error(`Export '${tabConfig.exportName}' not found`);
            }
        } catch (e) {
            console.error(e);
            contentArea.innerHTML = `
                <div class="bg-red-50 border border-red-200 text-red-600 p-6 rounded-xl text-center">
                    <span class="material-symbols-outlined text-4xl mb-2">error</span>
                    <p class="font-bold">Failed to load tab: ${tabConfig.label}</p>
                    <p class="text-sm opacity-80 mt-1">${e.message}</p>
                </div>
            `;
        }
    };

    // 3. Initial Load
    // [FIX] Support Query Param deep linking (e.g. ?tab=reports)
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');

    // Check hash as fallback (legacy & new router style)
    // Hash examples: '#overview', '#/admin/content', '#/admin/users'
    const hash = window.location.hash;
    let hashTab = null;

    if (hash.startsWith('#/admin/')) {
        // Extract 'content' from '#/admin/content'
        const parts = hash.split('/');
        if (parts.length >= 3) {
            hashTab = parts[2]; // 0=#, 1=admin, 2=content
        }
    } else {
        // Simple hash '#overview'
        hashTab = hash.replace('#', '');
    }

    let initialTab = TABS[0];

    if (tabParam) {
        initialTab = TABS.find(t => t.id === tabParam) || TABS[0];
    } else if (hashTab) {
        initialTab = TABS.find(t => t.id === hashTab) || TABS[0];
    }

    loadTab(initialTab);

    return container;
};
